import apiClient from './apiClient';

// ============================================================
// Types — aligned with backend models (backend/modules/pwg208/models/pwg208.go)
// ============================================================

export interface FG160 {
  fg160_01: string;       // 申請單號
  fg160_11: string | null; // 申請日期
  fg160_12: string;       // 業務員
  fg160_13: number;       // 傳送方式 (1=郵寄, 2=自取)
  fg160_14: number;       // 發票種類 (1~7)
  fg160_15: string;       // 開票對象
  fg160_16: string;       // 交運單號
  fg160_17: string;       // 部門
  fg160_18: string;       // 客戶代號
  fg160_19: string;       // 幣別
  fg160_20: number;       // 交運匯率
  fg160_21: number;       // 發票匯率
  fg160_22: number;       // 實際開票淨額
  fg160_221: number;      // 開票淨額小計
  fg160_23: number;       // 實際開票總額
  fg160_231: number;      // 開票總額小計
  fg160_24: string;       // 單據類別
  fg160_25: number;       // 開票總數量
  fg160_26: number;       // 平均單價
  fg160_27: number;       // 稅額
  fg160_28: number;       // 預收款金額
  fg160_29: number;       // 預收款含稅金額
  fg160_30: string;       // 報關單號
  fg160_31: number;       // 明細筆數
  fg160_32: string;       // 備註
  fg160_33: string;       // 關聯單號
  fg160_80: string;       // 狀態碼
  fg160_81: string;       // 財務送審人
  fg160_82: string;       // 立單核准人
  fg160_83: string;       // 財務核准人
  fg160_91: string;       // 建立人
  fg160_92: string | null; // 建立日期
  fg160_93: string;       // 建立時間
  // Associations
  details?: FG161[];
  prepayments?: FG163[];
  invoice_items?: FG164[];
  audit_logs?: FG16A[];
}

export interface FG161 {
  fg161_01: string;   // 申請單號
  fg161_02: number;   // 明細序號
  fg161_11: string;   // 單位
  fg161_12: string;   // 產品代號
  fg161_121: string;  // 產品代號2
  fg161_13: number;   // 交運數量
  fg161_14: number;   // 開票數量
  fg161_15: number;   // 交運單價(CF)
  fg161_16: number;   // 交運單價(CJ)
  fg161_17: number;   // 不含稅金額
  fg161_18: number;   // 含稅金額
  fg161_19: number;   // 本幣不含稅
  fg161_20: number;   // 本幣含稅
  fg161_21: string;   // 費用代號
  fg161_22: string;   // 發票品名
  fg161_23: number;   // 發票單價
  fg161_24: string;   // 發票單位
  fg161_25: number;   // 原始項次
  fg161_26: string;   // 產品規格
  fg161_27: number;   // 金額(備用)
  fg161_28: number;   // 金額(備用)
}

export interface FG163 {
  fg163_01: string;  // 申請單號
  fg163_02: number;  // 序號
  fg163_10: string;  // 預收款單號
  fg163_11: string;  // S/C No
  fg163_12: number;  // 預收款金額(CJ)
  fg163_13: number;  // 預收款金額(CF)
}

export interface FG164 {
  fg164_01: string;       // 申請單號
  fg164_02: number;       // 發票明細序號
  fg164_10: string;       // 發票號碼
  fg164_11: string | null; // 發票日期
  fg164_12: number;       // 發票金額(CJ含稅)
  fg164_13: number;       // 發票金額(CF未稅)
  fg164_91: string;       // 修改人
  fg164_92: string | null; // 修改時間
}

export interface FG16A {
  id: number;              // Web ERP 技術性主鍵
  fg16a_01: string;        // 申請單號
  fg16a_02: string;        // 操作人員代號
  fg16a_03: string | null; // 操作日期
  fg16a_04: string;        // 操作時間
  fg16a_11: string;        // 動作代碼
}

export interface CreatePWG208Request {
  master: Omit<FG160, 'details' | 'prepayments' | 'invoice_items' | 'audit_logs'>;
  details: FG161[];
  prepayments: FG163[];
  invoice_items: FG164[];
}

export interface UpdatePWG208Request {
  master: Omit<FG160, 'details' | 'prepayments' | 'invoice_items' | 'audit_logs'>;
  details: FG161[];
  prepayments: FG163[];
  invoice_items: FG164[];
}

export interface ListPWG208Response {
  data: FG160[];
  total: number;
  page: number;
  size: number;
}

// 狀態碼 → 顯示文字
export const STATUS_MAP: Record<string, { label: string; color: string }> = {
  A: { label: '草稿', color: 'default' },
  E: { label: '立單送審', color: 'processing' },
  P: { label: '立單核准', color: 'success' },
  C: { label: '作廢', color: 'error' },
  X: { label: '財務送審', color: 'warning' },
  Z: { label: '財務核准', color: 'success' },
};

// 傳送方式
export const SEND_TYPE_MAP: Record<number, string> = {
  1: '郵寄',
  2: '自取',
};

// 發票種類
export const INVOICE_TYPE_MAP: Record<number, string> = {
  1: '增值稅-三聯式',
  2: '普通發票-二聯式',
  3: '外銷發票-零稅率',
  4: '不開票-內銷',
  5: '不開票-外銷',
  6: '不開票-客訴',
  7: '不開票-銷貨折讓',
};

// 單據類別
export const DOC_TYPE_MAP: Record<string, string> = {
  A: '交運單',
  B: '樣品交運單',
  C: '庫存交運清單',
  D: '客戶寄庫交運單',
  E: '客訴買收交運單',
  F: '檢驗收入',
  G: '預收款',
  H: '代收代付',
  I: '其它應收',
  J: '客訴抵扣交運單',
  K: '銷貨折讓單',
};

// ============================================================
// API methods
// ============================================================

const BASE = '/api/pwg208';

export const pwg208Api = {
  async list(page = 1, size = 200): Promise<ListPWG208Response> {
    const res = await apiClient.get<ListPWG208Response>(BASE, { params: { page, size } });
    return res.data;
  },

  async getByID(id: string): Promise<FG160> {
    const res = await apiClient.get<FG160>(`${BASE}/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: CreatePWG208Request): Promise<FG160> {
    const res = await apiClient.post<FG160>(BASE, data);
    return res.data;
  },

  async update(id: string, data: UpdatePWG208Request): Promise<FG160> {
    const res = await apiClient.put<FG160>(`${BASE}/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
  },

  // Submit for approval: POST /api/pwg208/:id/submit (PWG208-ACT-SUBMIT).
  // No request body -- isFinanceUser is resolved server-side from the
  // caller's auth context (pwg208FinanceGate), not a client parameter.
  async submit(id: string): Promise<FG160> {
    const res = await apiClient.post<FG160>(`${BASE}/${encodeURIComponent(id)}/submit`);
    return res.data;
  },
};

export default pwg208Api;
